import { useEffect, useState } from 'react'
import {
  getAccountCapabilities,
  setAccountCapability,
  AccountCapabilities,
  CapabilityValue
} from '../lib/adminClient'
import {
  CAPABILITY_CATEGORIES,
  CAPABILITIES,
  Capability,
  capabilityKind
} from '../lib/capabilityCatalog'
import { tierName, tierTone } from '../lib/tiers'

const PRODUCTS_KEY = 'products'
const PRODUCT_CAPS = CAPABILITIES.filter((c) => c.categoryKey === PRODUCTS_KEY)
const OTHER_CATEGORIES = CAPABILITY_CATEGORIES.filter((c) => c.key !== PRODUCTS_KEY)

// Human-readable form of a resolved value for display next to a control.
function showValue(v: CapabilityValue | undefined): string {
  if (v === true) return 'On'
  if (v === false) return 'Off'
  if (v === undefined) return '—'
  return String(v)
}


interface RowProps {
  cap: Capability
  resolved: CapabilityValue | undefined
  overridden: boolean
  busy: boolean
  onSet: (value: CapabilityValue) => void
  onClear: () => void
}

function BooleanRow({ resolved, overridden, busy, onSet, onClear }: RowProps) {
  const on = resolved === true
  return (
    <div className="flex items-center gap-1 rounded-md border border-white/10 overflow-hidden text-[11px]">
      <button
        disabled={busy}
        onClick={() => onSet(true)}
        className={`px-2.5 py-1 ${overridden && on ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
      >
        On
      </button>
      <button
        disabled={busy}
        onClick={() => onSet(false)}
        className={`px-2.5 py-1 border-l border-white/10 ${overridden && !on ? 'bg-red-500/20 text-red-300' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
      >
        Off
      </button>
      <button
        disabled={busy}
        onClick={onClear}
        title="Clear the override and use the tier default"
        className={`px-2.5 py-1 border-l border-white/10 ${!overridden ? 'bg-white/10 text-slate-200' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
      >
        Default
      </button>
    </div>
  )
}

function NumberRow({ cap: _cap, resolved, overridden, busy, onSet, onClear }: RowProps) {
  const [draft, setDraft] = useState(typeof resolved === 'number' ? String(resolved) : '')
  // Keep the input in step with server truth when it changes underneath us.
  useEffect(() => {
    setDraft(typeof resolved === 'number' ? String(resolved) : '')
  }, [resolved])

  function commit() {
    const n = Number(draft)
    if (draft.trim() === '' || Number.isNaN(n)) return
    onSet(n)
  }

  return (
    <div className="flex items-center gap-2">
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit() }}
        placeholder={typeof resolved === 'number' ? '' : showValue(resolved)}
        className="w-24 bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-center focus:outline-none focus:border-accent"
      />
      <button
        disabled={busy}
        onClick={commit}
        className="px-2.5 py-1 rounded-md border border-white/10 text-[11px] text-slate-200 hover:bg-white/5 disabled:opacity-40"
      >
        Set
      </button>
      {overridden && (
        <button
          disabled={busy}
          onClick={onClear}
          className="text-[11px] text-slate-400 hover:text-slate-200 underline underline-offset-2 disabled:opacity-40"
        >
          Reset
        </button>
      )}
    </div>
  )
}

function StringRow({ resolved, overridden, busy, onClear }: RowProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-300">{showValue(resolved)}</span>
      <span className="text-[10px] text-slate-600">(read-only)</span>
      {overridden && (
        <button
          disabled={busy}
          onClick={onClear}
          className="text-[11px] text-slate-400 hover:text-slate-200 underline underline-offset-2 disabled:opacity-40"
        >
          Reset
        </button>
      )}
    </div>
  )
}

export default function AccessPanel({
  user,
  onClose
}: {
  user: { id: string; email: string; handle: string | null }
  onClose: () => void
}) {
  const [data, setData] = useState<AccountCapabilities | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setLoadError(null)
    void getAccountCapabilities(user.id).then((res) => {
      if (!alive) return
      if (!res.ok) {
        setLoadError(res.error || `Could not load access (HTTP ${res.status}).`)
        setLoading(false)
        return
      }
      const { storedTier, effectiveTier, resolved, overrides } = res.data
      setData({ storedTier, effectiveTier, resolved, overrides })
      setLoading(false)
    })
    return () => { alive = false }
  }, [user.id])

  async function apply(key: string, value: CapabilityValue | null) {
    setSavingKey(key)
    setActionError(null)
    const res = await setAccountCapability(user.id, key, value)
    setSavingKey(null)
    if (!res.ok) {
      setActionError(`${key}: ${res.error || `save failed (HTTP ${res.status})`}`)
      return
    }
    const { storedTier, effectiveTier, resolved, overrides } = res.data
    // The PUT returns the refreshed resolution, so we adopt it verbatim.
    setData({ storedTier, effectiveTier, resolved, overrides })
  }

  const overrideCount = data ? Object.keys(data.overrides).length : 0

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-6 overflow-y-auto" onClick={onClose}>
      <div className="glass-card rounded-2xl p-6 w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Access management</div>
            <h2 className="text-lg font-semibold mt-1">{user.email}</h2>
            <div className="text-[11px] text-slate-500">{user.handle ?? 'no handle'} · {user.id.slice(0, 18)}…</div>
          </div>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>

        {loading && (
          <div className="py-16 text-center text-sm text-slate-500">Loading this user’s access…</div>
        )}

        {!loading && loadError && (
          <div className="rounded-xl p-4 text-sm bg-red-500/10 border border-red-500/30 text-red-200">
            {loadError}
          </div>
        )}

        {!loading && data && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-5 text-xs text-slate-400">
              <span>Stored tier</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(data.storedTier)}`}>{tierName(data.storedTier)}</span>
              <span>Effective tier</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(data.effectiveTier)}`}>{tierName(data.effectiveTier)}</span>
              <span className="text-slate-500">·</span>
              <span>{overrideCount} {overrideCount === 1 ? 'custom override' : 'custom overrides'} on this user</span>
            </div>

            {actionError && (
              <div className="mb-4 rounded-md px-3 py-2 text-xs bg-red-500/10 border border-red-500/30 text-red-200">
                {actionError}
              </div>
            )}

            {/* Apps — the four products as prominent whole-app toggles. */}
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-1">Apps</div>
              <div className="text-[11px] text-slate-500 mb-3">Turn a whole app on or off for this user.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PRODUCT_CAPS.map((cap) => {
                  const on = data.resolved[cap.key] === true
                  const overridden = cap.key in data.overrides
                  const busy = savingKey === cap.key
                  return (
                    <div key={cap.key} className={`rounded-xl border p-4 ${on ? 'border-white/10 bg-white/[0.03]' : 'border-white/10 bg-black/20'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-slate-100 flex items-center gap-2">
                            {cap.label}
                            {overridden && (<span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/15 text-accent">custom</span>)}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{cap.description}</div>
                        </div>
                        <button
                          disabled={busy}
                          onClick={() => apply(cap.key, !on)}
                          aria-pressed={on}
                          className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 ${on ? 'bg-emerald-500/70' : 'bg-white/15'}`}
                          title={on ? 'On — click to turn off' : 'Off — click to turn on'}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      {overridden && (
                        <button
                          disabled={busy}
                          onClick={() => apply(cap.key, null)}
                          className="mt-2 text-[11px] text-slate-400 hover:text-slate-200 underline underline-offset-2 disabled:opacity-40"
                        >
                          Reset to tier default
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Everything else, grouped by category. */}
            {OTHER_CATEGORIES.map((cat) => {
              const items = CAPABILITIES.filter((c) => c.categoryKey === cat.key)
              if (items.length === 0) return null
              return (
                <div key={cat.key} className="mb-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-0.5">{cat.label}</div>
                  <div className="text-[11px] text-slate-500 mb-3">{cat.blurb}</div>
                  <div className="rounded-lg border border-white/5 overflow-hidden">
                    {items.map((cap) => {
                      const kind = capabilityKind(cap)
                      const resolved = data.resolved[cap.key]
                      const overridden = cap.key in data.overrides
                      const busy = savingKey === cap.key
                      const rowProps: RowProps = {
                        cap,
                        resolved,
                        overridden,
                        busy,
                        onSet: (v) => apply(cap.key, v),
                        onClear: () => apply(cap.key, null)
                      }
                      return (
                        <div key={cap.key} className={`flex items-center justify-between gap-4 px-4 py-3 border-b border-white/5 last:border-b-0 ${overridden ? 'bg-accent/[0.03]' : ''}`}>
                          <div className="min-w-0">
                            <div className="text-sm text-slate-100 flex items-center gap-2">
                              {cap.label}
                              {cap.status && cap.status !== 'shipping' && (
                                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cap.status === 'beta' ? 'bg-indigo-500/15 text-indigo-300' : 'bg-amber-500/15 text-amber-300'}`}>
                                  {cap.status === 'beta' ? 'Beta' : 'Soon'}
                                </span>
                              )}
                              {overridden && (<span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/15 text-accent">custom</span>)}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {overridden ? 'Custom for this user' : 'Inherited from tier'} · now <span className="text-slate-300">{showValue(resolved)}</span>
                            </div>
                          </div>
                          <div className="shrink-0">
                            {kind === 'boolean' && <BooleanRow {...rowProps} />}
                            {kind === 'number' && <NumberRow {...rowProps} />}
                            {kind === 'string' && <StringRow {...rowProps} />}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}

            <div className="text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3">
              Changes save the moment you toggle, set, or reset a row. Each change reverts to the tier default when cleared. String capabilities are shown read-only here; change them per tier from the Subscriptions page.
            </div>
          </>
        )}
      </div>
    </div>
  )
}
